import { Link, useParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import ComicCard from "@/components/feature/ComicCard";
import { comics } from "@/mocks/comics";

function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id) ?? comics[0];

  const related = comics
    .filter((c) => c.categoryId === comic.categoryId && c.id !== comic.id)
    .slice(0, 5);
  const relatedFill = related.length < 5 ? comics.filter((c) => c.id !== comic.id && !related.includes(c)).slice(0, 5 - related.length) : [];

  const chapters = Array.from({ length: 12 }, (_, i) => ({
    num: comic.chapters - i,
    title: i === 0 ? "最新章节" : `第 ${comic.chapters - i} 话`,
  }));

  return (
    <Layout
      title={`${comic.title}-${comic.category}|专属漫画|土豪漫画网`}
      description={`${comic.title}是土豪漫画网${comic.category}专区的高分神作，${comic.description}`}
    >
      <section className="relative overflow-hidden pt-28 md:pt-36">
        <div className="absolute inset-0">
          <img
            src={comic.cover}
            alt=""
            className="h-full w-full object-cover object-top opacity-20 blur-sm"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/80 via-background-950/90 to-background-950"></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 md:px-6 py-10">
          <nav className="text-xs text-foreground-500" aria-label="面包屑">
            <ol className="flex flex-wrap items-center gap-2">
              <li><span className="cursor-pointer">首页</span></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li>
                <Link to="/category" className="hover:text-primary-300">尊享专区</Link>
              </li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-primary-300">{comic.title}</li>
            </ol>
          </nav>

          <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <div className="relative overflow-hidden rounded-lg border border-primary-500/30 shadow-2xl shadow-primary-900/20">
                <img
                  src={comic.cover}
                  alt={`${comic.title}漫画封面`}
                  title={`${comic.title} · ${comic.category} · 专属漫画`}
                  className="aspect-[3/4] w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="flex flex-wrap items-center gap-2">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-primary-500/40 bg-primary-500/10 px-3 py-1 text-xs text-primary-300"
                  >
                    {tag}
                  </span>
                ))}
                {comic.isNew && (
                  <span className="whitespace-nowrap rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950">
                    新作上架
                  </span>
                )}
              </div>

              <h1 className="mt-4 font-heading text-3xl font-black text-foreground-50 md:text-5xl">
                {comic.title}
              </h1>
              <p className="mt-2 text-sm text-foreground-400">{comic.category} · {comic.status} · {comic.chapters}话</p>

              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div className="rounded-lg border border-background-800 bg-background-900/60 p-4">
                  <p className="text-xs text-foreground-500">作者</p>
                  <p className="mt-1 text-sm font-semibold text-foreground-100">{comic.author}</p>
                </div>
                <div className="rounded-lg border border-background-800 bg-background-900/60 p-4">
                  <p className="text-xs text-foreground-500">阅读量</p>
                  <p className="mt-1 text-sm font-semibold text-primary-300">{comic.views}</p>
                </div>
                <div className="rounded-lg border border-background-800 bg-background-900/60 p-4">
                  <p className="text-xs text-foreground-500">热度</p>
                  <p className="mt-1 text-sm font-semibold text-accent-300">{comic.hotScore}</p>
                </div>
                <div className="rounded-lg border border-background-800 bg-background-900/60 p-4">
                  <p className="text-xs text-foreground-500">评分</p>
                  <p className="mt-1 flex items-center gap-1 text-sm font-semibold text-foreground-100">
                    <i className="ri-star-fill text-primary-400"></i>
                    {comic.rating}
                  </p>
                </div>
              </div>

              <p className="mt-6 text-sm leading-7 text-foreground-300 md:text-base">{comic.description}</p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <span className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-gradient-to-r from-primary-500 to-accent-500 px-7 py-3.5 text-sm font-semibold text-foreground-950 transition hover:from-primary-400 hover:to-accent-400 cursor-pointer">
                  <i className="ri-play-fill text-lg"></i>
                  开始阅读
                </span>
                <span className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-7 py-3.5 text-sm font-semibold text-foreground-300 transition hover:border-primary-500/50 hover:text-primary-300 cursor-pointer">
                  <i className="ri-bookmark-line"></i>
                  加入书架
                </span>
                <span className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-7 py-3.5 text-sm font-semibold text-foreground-300 transition hover:border-primary-500/50 hover:text-primary-300 cursor-pointer">
                  <i className="ri-thumb-up-line"></i>
                  点赞
                </span>
              </div>

              <p className="mt-4 text-xs text-foreground-500">
                <i className="ri-time-line mr-1"></i>
                最近更新：{comic.updateTime} · 专属会员可抢先阅读最新章节
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-50 md:text-2xl">
              <span className="h-5 w-1 rounded-full bg-gradient-to-b from-primary-500 to-accent-500"></span>
              章节列表
            </h2>
            <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {chapters.map((ch) => (
                <li key={ch.num}>
                  <span className="flex items-center justify-between rounded-md border border-background-800 bg-background-900/50 px-4 py-3 text-sm text-foreground-300 transition hover:border-primary-500/40 hover:text-primary-300 cursor-pointer">
                    <span className="flex items-center gap-2">
                      {ch.num === comic.chapters && (
                        <i className="ri-flashlight-fill text-accent-400"></i>
                      )}
                      {ch.title}
                    </span>
                    <i className="ri-arrow-right-s-line text-foreground-600"></i>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-6 text-center">
              <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-5 py-2.5 text-sm font-medium text-foreground-400 transition hover:border-primary-500/40 hover:text-primary-300 cursor-pointer">
                查看全部 {comic.chapters} 话
                <i className="ri-arrow-down-line"></i>
              </span>
            </div>
          </div>

          <aside>
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-50 md:text-2xl">
              <span className="h-5 w-1 rounded-full bg-gradient-to-b from-primary-500 to-accent-500"></span>
              相关推荐
            </h2>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-2">
              {[...related, ...relatedFill].map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}

export default ComicDetail;